import { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Globe,
  Link2,
  MessageSquare,
  RefreshCw,
  Settings,
  Star,
  XCircle,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

interface GbpStatus {
  connected: boolean;
  accountName?: string;
  locationName?: string;
  locationId?: string;
  connectedAt?: string | null;
  lastSyncAt?: string | null;
  syncStatus: "idle" | "success" | "error";
  syncErrors?: string[];
  reviewSummary: {
    totalReviews: number;
    averageRating: number;
  };
  supportedSyncCapabilities: {
    reviews: boolean;
    businessInfo: boolean;
    hours: boolean;
    photos: boolean;
    posts: boolean;
    qna: boolean;
  };
}

interface SyncResponse {
  synced: boolean;
  mode: "reviews_only";
  fetchedReviews: number;
  newReviews: number;
  updatedReviews: number;
  unsupported: string[];
}

function CapabilityRow({
  label,
  available,
  detail,
}: {
  label: string;
  available: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-background px-4 py-3">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
      </div>
      {available ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-success)]" />
      ) : (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </div>
  );
}

export default function GbpSync() {
  const { data, isLoading, error, refetch } = useApi<GbpStatus>("/crm/reviews/gbp/status");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResponse | null>(null);

  const queryState = useMemo(() => {
    if (typeof window === "undefined") {
      return { connected: false, error: null as string | null };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      connected: params.get("connected") === "google_business_profile",
      error: params.get("error"),
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setActionError(null);
    try {
      const response = await apiFetch<{ data: { authUrl: string } }>("/crm/reviews/gbp/connect", {
        method: "POST",
        body: "{}",
      });
      window.location.href = response.data.authUrl;
    } catch (connectError) {
      setActionError(connectError instanceof Error ? connectError.message : "Failed to start Google authorization");
      setIsConnecting(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setActionError(null);
    try {
      const response = await apiFetch<{ data: SyncResponse }>("/crm/reviews/gbp/sync", {
        method: "POST",
      });
      setSyncResult(response.data);
      refetch();
    } catch (syncError) {
      setActionError(syncError instanceof Error ? syncError.message : "Failed to sync Google Business Profile reviews");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm("Disconnect Google Business Profile? Review sync will stop until you reconnect.");
    if (!confirmed) {
      return;
    }

    setIsDisconnecting(true);
    setActionError(null);
    try {
      await apiFetch("/crm/reviews/gbp/disconnect", { method: "POST" });
      setSyncResult(null);
      refetch();
    } catch (disconnectError) {
      setActionError(disconnectError instanceof Error ? disconnectError.message : "Failed to disconnect Google Business Profile");
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load Google Business Profile status.
      </div>
    );
  }

  const supported = data?.supportedSyncCapabilities ?? {
    reviews: true,
    businessInfo: false,
    hours: false,
    photos: false,
    posts: false,
    qna: false,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Google Business Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          This mounted seam is currently truthful for review connection and review sync only. Business profile fields, hours, photos, posts, and Q&amp;A are not yet writable from this surface.
        </p>
      </div>

      {queryState.connected && (
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4 text-sm text-[var(--color-success)]">
          Google Business Profile connected successfully. Run a review sync to pull the latest GBP reviews into the reputation dashboard.
        </div>
      )}

      {queryState.error && (
        <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          Google Business Profile connection failed: {queryState.error}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          {actionError}
        </div>
      )}

      {!data?.connected ? (
        <div className="max-w-xl rounded-2xl border border-border bg-background p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-primary-light)]">
              <Globe className="h-6 w-6 text-[var(--color-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Connect Google Business Profile</h2>
              <p className="text-sm text-muted-foreground">
                Authorize one GBP location so Stero can import reviews through the existing review-source runtime.
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <CapabilityRow label="Review sync" available detail="Imports GBP reviews into the reputation dashboard and keeps review replies on the same provider seam." />
            <CapabilityRow label="Profile data sync" available={false} detail="Business info and hours are still outside the mounted GBP runtime for this stream item." />
            <CapabilityRow label="Photos, posts, and Q&A" available={false} detail="Those surfaces need a dedicated schema-backed sync path before they can be exposed honestly." />
          </div>

          <button
            type="button"
            onClick={handleConnect}
            disabled={isConnecting}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            {isConnecting ? "Redirecting..." : "Connect with Google"}
          </button>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-background p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
                  <p className="text-sm font-medium text-foreground">Connected</p>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {data.locationName ?? data.accountName}
                  {data.locationId ? ` • ${data.locationId}` : ""}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Connected {data.connectedAt ? formatDateTime(data.connectedAt) : "recently"}
                  {data.lastSyncAt ? ` • Last sync ${formatDateTime(data.lastSyncAt)}` : ""}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
                  {isSyncing ? "Syncing reviews..." : "Sync Reviews"}
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                >
                  <Settings className="h-4 w-4" />
                  {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
            </div>

            {data.syncErrors && data.syncErrors.length > 0 && (
              <div className="mt-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3">
                {data.syncErrors.map((syncError) => (
                  <p key={syncError} className="text-xs text-[var(--color-destructive)]">{syncError}</p>
                ))}
              </div>
            )}

            {syncResult && (
              <div className="mt-4 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4">
                <p className="text-sm font-medium text-foreground">Manual sync completed</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pulled {syncResult.fetchedReviews} reviews. {syncResult.newReviews} new, {syncResult.updatedReviews} updated.
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-background p-5">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-[var(--color-primary)]" />
                <p className="text-sm font-medium text-foreground">Review coverage</p>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-semibold text-foreground">{data.reviewSummary.totalReviews}</p>
                  <p className="text-xs text-muted-foreground">Imported reviews</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {data.reviewSummary.averageRating > 0 ? data.reviewSummary.averageRating.toFixed(1) : "0.0"}
                  </p>
                  <p className="text-xs text-muted-foreground">Average rating</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-5">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-[var(--color-primary)]" />
                <p className="text-sm font-medium text-foreground">Mounted capabilities</p>
              </div>
              <div className="mt-4 space-y-3">
                <CapabilityRow label="Reviews" available={supported.reviews} detail="Mounted on `/crm|/v1/reviews/gbp/*` and backed by `review_sources` plus `oauth_integrations`." />
                <CapabilityRow label="Profile and hours" available={supported.businessInfo || supported.hours} detail="Not mounted yet on this seam. No fake profile writes are exposed here." />
                <CapabilityRow label="Photos, posts, and Q&A" available={supported.photos || supported.posts || supported.qna} detail="Requires a dedicated GBP content sync model before this page can expose those actions honestly." />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
